import { useState, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Cuisine / Category combobox with inline "Create new" affordance.
 *
 * Props:
 *  - value: current selected value (string)
 *  - options: string[] of currently available values
 *  - placeholder: input placeholder
 *  - onChange(value): called when user picks a value
 *  - onCreate(value): optional — if provided AND user types a value not in options,
 *                     a "+ Create '<value>'" item appears. Returns a Promise<void>.
 *  - testIdPrefix: e.g. 'recipe-cuisine'
 *  - clearable: bool — show inline X to clear (default true)
 */
export function TaxonomySelect({
  value = '',
  options = [],
  placeholder = 'Select…',
  onChange,
  onCreate,
  testIdPrefix = 'taxonomy',
  clearable = true,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const trimmed = search.trim();
  const matchingExists = useMemo(
    () => options.some(o => (o || '').toLowerCase() === trimmed.toLowerCase()),
    [options, trimmed],
  );
  const canCreate = !!onCreate && trimmed.length > 0 && trimmed.length <= 60 && !matchingExists;

  const handleSelect = (opt) => {
    onChange?.(opt);
    setSearch('');
    setOpen(false);
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      await onCreate(trimmed);
      onChange?.(trimmed);
      setSearch('');
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-trigger`}
          className={cn(
            'h-9 w-full px-3 rounded-md border border-[#E2E8DF] bg-white text-left',
            'flex items-center justify-between gap-1.5',
            'text-[13px] transition-colors',
            'hover:border-[#A0846B] focus:outline-none focus:ring-2 focus:ring-[#E8643E]/30',
            disabled && 'opacity-50 cursor-not-allowed',
            !value && 'text-[#9CA9A1]',
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {clearable && value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); onChange?.(''); }}
                data-testid={`${testIdPrefix}-clear`}
                className="w-4 h-4 rounded-sm text-[#5C6B64] hover:bg-[#F3F5F2] flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </span>
            )}
            <ChevronDown className="w-3.5 h-3.5 text-[#5C6B64]" strokeWidth={1.75} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px] border-[#E2E8DF]"
        data-testid={`${testIdPrefix}-popover`}
      >
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Type to search or create…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${testIdPrefix}-search`}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {canCreate ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid={`${testIdPrefix}-create-from-empty`}
                  className="w-full px-3 py-2 text-[13px] text-left text-[#7A2A12] hover:bg-[#FCE7D6] rounded-sm flex items-center gap-2"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Create &ldquo;{trimmed}&rdquo;
                </button>
              ) : (
                <div className="py-4 text-center text-[12px] text-[#5C6B64]">
                  No matches
                </div>
              )}
            </CommandEmpty>
            {canCreate && (
              <CommandGroup heading="Add new">
                <CommandItem
                  value={`__create__${trimmed}`}
                  onSelect={handleCreate}
                  data-testid={`${testIdPrefix}-create-option`}
                  className="text-[#7A2A12] data-[selected=true]:bg-[#FCE7D6]"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" />
                  Create &ldquo;{trimmed}&rdquo;
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={options.length > 0 ? 'Available' : ''}>
              {options.map(opt => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => handleSelect(opt)}
                  data-testid={`${testIdPrefix}-option-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[13px]"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === opt ? 'opacity-100 text-[#E8643E]' : 'opacity-0',
                    )}
                  />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
